import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Skeleton,
  InputAdornment,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Grid2,
} from '@mui/material';
import Header from '../Header';
import Footer from '../Footer';
import { API_URL } from '../Config';
import config from "../common/Config";
import { Iconify } from "src/components/iconify";
import { useLocation } from 'react-router-dom';
import '../common/commonStyles.css';
import { postPickingHistory } from "../../utils/pickingApi";

const PickingHistory = () => {
  const location = useLocation();
  const inTabs = location.pathname === '/picking/picking_list';
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [newSales, setNewSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const accessToken = localStorage.getItem('accessToken');

  const getColumns = () => {
    const baseColumns = [
      { id: "picklist_number", label: "Picklist Number", width: 150 },
    ];

    if (accessToken === 'tss' || accessToken === 'tsc') {
      baseColumns.push(
        { id: "reference_no", label: "Order ID", width: 120 },
        { id: "customer", label: "Customer Name", width: 200 },
        { id: "route", label: "Route Name", width: 150 }
      );
    } else {
      baseColumns.push(
        { id: "route", label: "Route Name", width: 150 }
      );
    }

    baseColumns.push(
      { id: "total", label: "Total", width: 100 },
      { id: "picked", label: "Picked", width: 100 },
      { id: "picker", label: "Picker", width: 200 },
      { id: "picklist_date", label: "Created Date", width: 150 },
      { id: "picked_date", label: "Completed Date", width: 150 },
      { id: "picking_status", label: "Picking Status", width: 150 },
      { id: "action", label: "Action", width: 100 }
    );

    return baseColumns;
  };

  const columns = getColumns();

  const fetchData = async () => {
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken === null) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }
    const token = localStorage.getItem("token");

    setLoading(true);
    try {
      const data = await postPickingHistory({
        searchByFromdate: fromDate,
        searchByTodate: toDate,
        draw: "1",
        start: "0",
        length: "1000",
        role: selectedRole,
      }, token);
      setNewSales(Array.isArray(data.data) ? data.data : []);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fromDate, toDate, selectedRole]);

  const filteredSales = useMemo(() => {
    if (!searchTerm.trim()) {
      return newSales;
    }
    const searchLower = searchTerm.toLowerCase().trim();
    return newSales.filter((item) => {
      return (
        (item.picklist_number && String(item.picklist_number).toLowerCase().includes(searchLower)) ||
        (item.reference_no && String(item.reference_no).toLowerCase().includes(searchLower)) ||
        (item.customer && item.customer.toLowerCase().includes(searchLower)) ||
        (item.route && item.route.toLowerCase().includes(searchLower)) ||
        (item.picker && item.picker.toLowerCase().includes(searchLower)) ||
        (item.picking_status && item.picking_status.toLowerCase().includes(searchLower)) ||
        (item.picklist_date && item.picklist_date.toLowerCase().includes(searchLower)) ||
        (item.picked_date && String(item.picked_date).toLowerCase().includes(searchLower))
      );
    });
  }, [newSales, searchTerm]);

  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedSales = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredSales.slice(startIndex, endIndex);
  }, [filteredSales, page, rowsPerPage]);

  const handleMenuOpen = (event, row) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = (event) => {
    if (event) {
      event.stopPropagation();
    }
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleView = () => {
    if (selectedRow) {
      window.location.href = `${config.BASE_URL}/picking/view_picking/${selectedRow.picklist_number}`;
    }
    handleMenuClose();
  };

  const renderCell = (columnId, row) => {
    if (columnId === "total") {
      const products = row.total_product ?? 0;
      const qty = row.total_qty ?? 0;
      return `${products}/${qty}`;
    }
    if (columnId === "picked") {
      const pickedProducts = row.total_picked_product ?? 0;
      const pickedQty = row.total_picked_qty ?? 0;
      return `${pickedProducts}/${pickedQty}`;
    }
    if (columnId === "picking_status") {
      const color = row.picking_status === "Completed" ? "success" : row.picking_status === "Pending" ? "warning" : "default";
      return (
        <Chip
          label={row.picking_status}
          color={color}
          variant="soft"
          size="small"
          sx={{ fontWeight: 500, fontSize: "0.7rem", px: 1 }}
        />
      );
    }
    if (columnId === "action") {
      return (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <IconButton
            size="small"
            onClick={(e) => handleMenuOpen(e, row)}
            sx={{ color: "#637381" }}
          >
            <Iconify icon="eva:more-vertical-fill" width={20} height={20} />
          </IconButton>
        </Box>
      );
    }
    return row[columnId] ?? "";
  };

  return (
    <>
      {!inTabs && <Header />}
      <Box sx={{ width: "100%", px: inTabs ? 0 : { xs: 2, md: 3 }, py: inTabs ? 0 : 2, backgroundColor: inTabs ? "transparent" : "#f5f7fa", height: inTabs ? "auto" : "calc(100vh - 110px)" }}>
        <Paper
        >
          <Box sx={{ px: 2, pb: 1}}>
            <Grid2 container spacing={2} alignItems="center">
            <Grid2 size={{ xs: 12, md: 8 }}>
              <Typography className="page-title">
                Picking History
              </Typography>
            </Grid2>
              <Grid2 size={{ xs: 12, md: 4 }}>
                <TextField
                  className="input-border"
                  fullWidth
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="small"
                  placeholder="Search picking history..."
                  variant="outlined"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Iconify icon="eva:search-outline"/>
                        </InputAdornment>
                      )
                    }
                  }}
               
                />
              </Grid2>
            </Grid2>
          </Box>

          <TableContainer 
          >
            <Table
              size="medium"
              className="table-cell-border"
            >
              <colgroup>
                {columns.map((column) => (
                  <col key={column.id} style={{ width: column.width ? `${column.width}px` : "auto" }} />
                ))}
              </colgroup>
              <TableHead>
                <TableRow className="table-cell-header">
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.id === "action" ? "center" : column.numeric ? "right" : "left"}
                      sx={{
                        width: { xs: "auto", sm: column.width ? `${column.width}px` : "auto" },
                        minWidth: { xs: column.width ? `${Math.min(column.width, 100)}px` : "auto", sm: column.width ? `${column.width}px` : "auto" }
                      }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: 10 }).map((_, index) => (
                    <TableRow key={`skeleton-${index}`}>
                      {columns.map((column) => (
                        <TableCell
                          key={column.id}
                          align={column.id === "action" ? "center" : column.numeric ? "right" : "left"}
                          sx={{ py: { xs: 1, sm: 1.5 }, px: { xs: 1, sm: 2 }, fontSize: { xs: "0.7rem", sm: "0.75rem" }, width: { xs: "auto", sm: column.width ? `${column.width}px` : "auto" } }}
                        >
                          <Skeleton variant="text" width="60%" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : paginatedSales.length > 0 ? (
                  paginatedSales.map((row) => (
                    <TableRow
                      key={row.picklist_number}
                      hover
                      className="table-style"
                    >
                      {columns.map((column) => (
                        <TableCell
                          key={column.id}
                          align={column.id === "action" ? "center" : column.numeric ? "right" : "left"}
                          sx={{
                            py: { xs: 1, sm: 1},
                            px: { xs: 1, sm: 2 },
                            fontSize: { xs: "0.7rem", sm: "0.75rem" },
                            color: "#212b36",
                            width: { xs: "auto", sm: column.width ? `${column.width}px` : "auto" },
                            wordBreak: column.id === "customer" || column.id === "picker" ? 'break-word' : 'normal',
                            ...(column.numeric && { fontWeight: 500 })
                          }}
                        >
                          {renderCell(column.id, row)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} sx={{ py: 8 }}>
                      <Typography align="center" sx={{ color: "#637381", fontSize: { xs: "0.7rem", sm: "0.75rem" } }}>
                        No picking history found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <TablePagination
              component="div"
              count={filteredSales.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              className="pagination-styles"
            />
          </Box>
        </Paper>
      </Box>
      {!inTabs && <Footer />}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 160,
            boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
          },
        }}
      >
        <MenuItem onClick={handleView}>
          <ListItemIcon>
            <Iconify icon="eva:eye-fill" width={20} height={20}/>
          </ListItemIcon>
          <ListItemText>View</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default PickingHistory;